import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle } from "lucide-react";
import {
  useTaxObligations,
  useUpdateTaxObligation,
  TAX_STATUS_LABEL,
  TaxObligationStatus,
  taxObligationClientName,
  isTaxOverdue,
} from "@/api/taxes";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";
import { getApiErrorMessage } from "@/api/client";
import CreateTaxObligationModal from "./CreateTaxObligationModal";

const STATUS_STYLE: Record<TaxObligationStatus, string> = {
  PENDIENTE: "bg-gray-100 text-gray-600",
  EN_PREPARACION: "bg-blue-100 text-blue-700",
  EN_REVISION: "bg-purple-100 text-purple-700",
  PRESENTADO: "bg-teal-100 text-teal-700",
  PAGADO: "bg-green-100 text-green-700",
  VENCIDO: "bg-red-100 text-red-700",
};

const STATUS_OPTIONS = Object.keys(TAX_STATUS_LABEL) as TaxObligationStatus[];

export default function TaxesPage() {
  const [status, setStatus] = useState<TaxObligationStatus | "">("");
  const [search, setSearch] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useTaxObligations({
    status: status || undefined,
    search: search || undefined,
    overdue: overdue || undefined,
    page,
    pageSize: 15,
  });
  const updateTaxObligation = useUpdateTaxObligation();

  const taxObligations = data?.data ?? [];
  const meta = data?.meta;

  const handleStatusChange = async (id: string, newStatus: TaxObligationStatus) => {
    try {
      await updateTaxObligation.mutateAsync({ id, status: newStatus });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Impuestos</h1>
          <p className="text-sm text-gray-500">Obligaciones tributarias (IVA, retención, renta, ICA) con su calendario de vencimientos por cliente.</p>
        </div>
        {hasPermission("taxes.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nueva obligación
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por tipo, periodo o confirmación..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as TaxObligationStatus | "");
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {TAX_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked);
              setPage(1);
            }}
          />
          Solo vencidas
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Impuesto</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Periodo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Vence</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && taxObligations.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                  No hay obligaciones tributarias que coincidan con los filtros.
                </td>
              </tr>
            )}
            {taxObligations.map((taxObligation) => (
              <tr key={taxObligation.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{taxObligation.taxType}</p>
                  {taxObligation.amount && (
                    <p className="text-xs text-gray-400">
                      {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(
                        Number(taxObligation.amount)
                      )}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {taxObligation.client ? (
                    <Link to={`/clients/${taxObligation.client.id}`} className="hover:text-brand-600">
                      {taxObligationClientName(taxObligation)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{taxObligation.periodLabel}</td>
                <td className="px-4 py-3">
                  {taxObligation.dueDate ? (
                    <span className={isTaxOverdue(taxObligation) ? "inline-flex items-center gap-1 text-red-600" : "text-gray-600"}>
                      {isTaxOverdue(taxObligation) && <AlertTriangle size={12} />}
                      {taxObligation.dueDate}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {hasPermission("taxes.update") ? (
                    <select
                      className={`rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[taxObligation.status]}`}
                      value={taxObligation.status}
                      onChange={(e) => handleStatusChange(taxObligation.id, e.target.value as TaxObligationStatus)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {TAX_STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[taxObligation.status]}`}>
                      {TAX_STATUS_LABEL[taxObligation.status]}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} obligaciones)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateTaxObligationModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
